(function () {
  'use strict';

  /**
   * Native product configurator — hooks into the main PDP <product-form> submit.
   * When the customer has configurator selections, we cancel the default single-item
   * submit and POST a multi-item payload to /cart/add.js (main variant + extras).
   * Non-selection case is a no-op: the normal product-form flow runs untouched.
   */

  function init() {
    document.querySelectorAll('[data-configurator]').forEach(setup);
  }

  function setup(root) {
    if (root.__configuratorInit) return;
    root.__configuratorInit = true;

    var productForm = findProductForm(root);
    if (!productForm) {
      console.warn('[configurator] no <product-form> found for', root);
      return;
    }
    var form = productForm.querySelector('.js-product-form') || productForm.querySelector('form');
    if (!form) return;

    // When product-upsells is present on the page, it owns the multi-item submit
    // (it reads our configurator selections too). Both handlers installing
    // capture-phase stopImmediatePropagation would cancel each other, so we
    // only attach ours when upsells aren't rendered. Both pages load the script;
    // only the DOM element .product-upsells signals that the upsell handler has
    // items to care about.
    var upsellsPresent = !!document.querySelector('.product-upsells');
    if (!upsellsPresent) {
      // Capture phase so we run BEFORE product-form's bubble listener, and
      // stopImmediatePropagation below blocks it when we have extras.
      form.addEventListener('submit', function (e) {
        var selections = getSelections(root);
        if (selections.length === 0) return; // let normal ATC flow run

        e.preventDefault();
        e.stopImmediatePropagation();
        submitWithExtras(productForm, form, selections);
      }, true);
    }

    setupPriceSync(root);
  }

  function setupPriceSync(root) {
    var section = root.closest('.shopify-section') || document;
    var priceValues = section.querySelectorAll('.price .price__current .js-value');
    if (priceValues.length === 0) return;

    var affirmEl = section.querySelector('.affirm-as-low-as');
    var basePrice = parseInt(root.dataset.basePrice, 10) || 0;
    var originalHtml = priceValues[0].innerHTML;

    function selectionTotalCents() {
      var total = 0;
      root.querySelectorAll('[data-configurator-slot]').forEach(function (slot) {
        var selected = slot.querySelector('custom-select [aria-selected="true"]');
        if (selected && selected.dataset.price) {
          total += parseInt(selected.dataset.price, 10) || 0;
        }
      });
      return total;
    }

    function renderPrice() {
      var extras = selectionTotalCents();
      if (extras === 0) {
        priceValues.forEach(function (el) { el.innerHTML = originalHtml; });
        if (affirmEl) affirmEl.setAttribute('data-amount', basePrice);
        return;
      }
      var total = basePrice + extras;
      var formatted = formatMoneyUSD(total);
      priceValues.forEach(function (el) { el.textContent = formatted; });
      if (affirmEl) affirmEl.setAttribute('data-amount', total);
    }

    // Listen for changes from any custom-select inside our slots.
    root.addEventListener('change', function (e) {
      if (e.target.tagName && e.target.tagName.toLowerCase() === 'custom-select') {
        renderPrice();
      }
    });

    // When the main variant changes (Color: Black -> Silver), re-capture the
    // new base price + the theme's freshly rendered original HTML, then reapply.
    document.addEventListener('on:variant:change', function (e) {
      if (!e.detail || !e.detail.variant) return;
      basePrice = e.detail.variant.price || basePrice;
      root.dataset.basePrice = basePrice;
      setTimeout(function () {
        if (priceValues[0]) originalHtml = priceValues[0].innerHTML;
        renderPrice();
      }, 50);
    });
  }

  function formatMoneyUSD(cents) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function findProductForm(root) {
    // Prefer the product-form inside the same Shopify section as the configurator.
    var section = root.closest('.shopify-section') || document;
    return section.querySelector('product-form') || document.querySelector('product-form');
  }

  function getSelections(root) {
    var out = [];
    root.querySelectorAll('[data-configurator-slot]').forEach(function (slot) {
      var selected = slot.querySelector('custom-select [aria-selected="true"]');
      if (selected && selected.dataset.variantId) {
        var id = parseInt(selected.dataset.variantId, 10);
        if (id) out.push(id);
      }
    });
    return out;
  }

  function submitWithExtras(productForm, form, selections) {
    var submitBtn = productForm.querySelector('[name="add"]');
    if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') return;

    if (submitBtn) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');
    }

    var mainId = currentVariantId(form);
    if (!mainId) {
      console.error('[configurator] main variant id missing on form');
      resetBtn(submitBtn);
      return;
    }

    var qtyInput = form.querySelector('input[name="quantity"]');
    var mainQty = (qtyInput && qtyInput.value) ? (parseInt(qtyInput.value, 10) || 1) : 1;

    var items = [{ id: mainId, quantity: mainQty }];
    selections.forEach(function (id) {
      items.push({ id: id, quantity: 1 });
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (resp) {
        if (!resp.ok) return resp.json().then(function (err) { throw err; });
        return resp.json();
      })
      .then(function () { afterAddSuccess(productForm, submitBtn); })
      .catch(function (err) {
        console.error('[configurator] cart add failed:', err);
        var msg = (err && err.description) || (err && err.message) ||
          'Could not add to cart. Please try again or call (410) 778-0332.';
        alert(msg);
        resetBtn(submitBtn);
      });
  }

  // The product form's own input[name="id"] is rendered disabled and never updated on a
  // variant change (main-product.liquid), so reading it sent the DEFAULT variant whenever
  // a configurator extra was picked (TASK-1247, Hana Trade Up). The variant picker holds
  // the selected variant; fall back to an enabled id control for single-variant products.
  function currentVariantId(form) {
    var section = form.closest('.shopify-section') || document;
    var picker = section.querySelector('variant-picker');
    if (picker && picker.variant && picker.variant.id) return parseInt(picker.variant.id, 10);
    var el = Array.prototype.find.call(form.elements, function (x) {
      return x.name === 'id' && !x.disabled && x.value;
    }) || form.querySelector('[name="id"]');
    return el && el.value ? parseInt(el.value, 10) : 0;
  }

  function afterAddSuccess(productForm, submitBtn) {
    var t = window.theme || {};
    var settings = t.settings || {};
    var routes = t.routes || {};
    var afterAtc = settings.afterAtc || 'page';

    if (afterAtc === 'page') {
      setTimeout(function () {
        window.location.href = routes.cart || '/cart';
      }, 300);
      return;
    }

    // Drawer / notification modes — refresh drawer + icon so extras show up.
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.refresh === 'function') {
      cartDrawer.refresh(afterAtc === 'drawer');
    }
    updateCartIconBubble();
    resetBtn(submitBtn, true);
  }

  function updateCartIconBubble() {
    fetch(window.location.pathname + '?sections=cart-icon-bubble')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data['cart-icon-bubble']) return;
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) bubble.innerHTML = data['cart-icon-bubble'];
      })
      .catch(function () {});
  }

  function resetBtn(btn, success) {
    if (!btn) return;
    if (success) btn.classList.add('is-success');
    btn.removeAttribute('aria-disabled');
    setTimeout(function () {
      btn.classList.remove('is-loading');
      btn.classList.remove('is-success');
    }, 1400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
